import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { apiBaseUrl } from '../../config/runtime';
import './NpsManager.css';

const VIEW_OPTIONS = [
  { id: 'coleta', label: 'Operacao de Coleta' },
  { id: 'perguntas', label: 'Formulario da Pesquisa' },
  { id: 'gestao', label: 'Inteligencia NPS' },
];

const QUESTION_TYPE_OPTIONS = [
  { value: 'score_0_10', label: 'Nota 0-10' },
  { value: 'text', label: 'Texto curto' },
  { value: 'textarea', label: 'Texto longo' },
  { value: 'yes_no', label: 'Sim ou Nao' },
];

const QUESTION_TYPE_LABEL = {
  score_0_10: 'Nota 0-10',
  text: 'Texto curto',
  textarea: 'Texto longo',
  yes_no: 'Sim ou Nao',
};

const CLASSIFICATION_LABEL = {
  promotor: 'Promotor',
  neutro: 'Neutro',
  detrator: 'Detrator',
  nao_classificado: 'Nao classificado',
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '-' : parsed.toLocaleString('pt-BR');
};

const normalizeYesNo = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return null;
  if (['sim', 's', 'yes', 'y', '1', 'true'].includes(raw)) return 'Sim';
  if (['nao', 'n', 'no', '0', 'false'].includes(raw)) return 'Nao';
  return null;
};

const normalizeAnswerMap = (answers) => {
  const mapped = {};
  if (!Array.isArray(answers)) {
    return mapped;
  }
  answers.forEach((item) => {
    const questionId = item?.question_id;
    if (!questionId) return;
    if (item.answer_numeric !== null && item.answer_numeric !== undefined) {
      mapped[String(questionId)] = String(item.answer_numeric);
      return;
    }
    const textValue = item.answer_text ?? item.answer;
    mapped[String(questionId)] = textValue === null || textValue === undefined ? '' : String(textValue);
  });
  return mapped;
};

const NpsManager = () => {
  const [activeView, setActiveView] = useState('coleta');

  const [pending, setPending] = useState([]);
  const [pendingSearch, setPendingSearch] = useState('');
  const [pendingMode, setPendingMode] = useState('all');
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);

  const [questions, setQuestions] = useState([]);
  const [answerValues, setAnswerValues] = useState({});
  const [feedback, setFeedback] = useState('');

  const [management, setManagement] = useState({
    summary: {},
    matrix: [],
    rows: [],
    questions: [],
    question_stats: [],
  });
  const [managementStart, setManagementStart] = useState('');
  const [managementEnd, setManagementEnd] = useState('');
  const [managementSearch, setManagementSearch] = useState('');
  const [managementClassification, setManagementClassification] = useState('all');

  const [newQuestion, setNewQuestion] = useState({
    question_text: '',
    question_type: 'text',
    is_required: false,
    is_active: true,
    is_primary: false,
  });

  const [loadingPending, setLoadingPending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [loadingManagement, setLoadingManagement] = useState(false);
  const [savingNps, setSavingNps] = useState(false);
  const [creatingQuestion, setCreatingQuestion] = useState(false);
  const [questionBusyId, setQuestionBusyId] = useState(null);
  const [seedLoading, setSeedLoading] = useState(false);

  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const orderedQuestions = useMemo(
    () => [...questions].sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || (a.id ?? 0) - (b.id ?? 0)),
    [questions]
  );

  const activeQuestions = useMemo(() => orderedQuestions.filter((question) => question.is_active), [orderedQuestions]);

  const primaryQuestion = useMemo(() => {
    const explicit = activeQuestions.find((question) => question.is_primary);
    if (explicit) return explicit;
    return activeQuestions.find((question) => question.question_type === 'score_0_10') || null;
  }, [activeQuestions]);

  const filteredPending = useMemo(() => {
    const term = pendingSearch.trim().toLowerCase();
    return pending.filter((item) => {
      if (pendingMode === 'imediata' && item.queue_reason !== 'coleta_imediata') return false;
      if (pendingMode === 'retroativa' && item.queue_reason !== 'retroativo_sem_nps') return false;
      if (!term) return true;
      const text = `${item.ticket_number || ''} ${item.customer_name || ''} ${item.customer_phone || ''} ${item.status || ''}`.toLowerCase();
      return text.includes(term);
    });
  }, [pending, pendingMode, pendingSearch]);

  const pendingSummary = useMemo(() => {
    let imediata = 0;
    let retroativa = 0;
    pending.forEach((item) => {
      if (item.queue_reason === 'coleta_imediata') {
        imediata += 1;
      } else {
        retroativa += 1;
      }
    });
    return {
      total: pending.length,
      imediata,
      retroativa,
    };
  }, [pending]);

  const filteredManagementRows = useMemo(() => {
    const term = managementSearch.trim().toLowerCase();
    return (management.rows || []).filter((row) => {
      const matchesClass = managementClassification === 'all' || row.classification === managementClassification;
      if (!matchesClass) return false;
      if (!term) return true;
      const text = `${row.ticket_number || ''} ${row.customer_name || ''} ${row.customer_phone || ''} ${row.atendente || ''} ${row.feedback || ''}`.toLowerCase();
      return text.includes(term);
    });
  }, [management.rows, managementSearch, managementClassification]);

  const loadQuestions = useCallback(async () => {
    try {
      setLoadingQuestions(true);
      const res = await axios.get(`${apiBaseUrl}/nps/questions`, {
        params: { include_inactive: 1 },
      });
      setQuestions(Array.isArray(res.data) ? res.data : []);
    } catch (loadError) {
      setQuestions([]);
    } finally {
      setLoadingQuestions(false);
    }
  }, []);

  const loadPending = useCallback(async () => {
    try {
      setLoadingPending(true);
      const res = await axios.get(`${apiBaseUrl}/nps/pending`);
      setPending(Array.isArray(res.data) ? res.data : []);
    } catch (loadError) {
      setPending([]);
    } finally {
      setLoadingPending(false);
    }
  }, []);

  const loadHistory = useCallback(async (customerId) => {
    if (!customerId) {
      setHistory([]);
      return;
    }
    try {
      setLoadingHistory(true);
      const res = await axios.get(`${apiBaseUrl}/nps/customer/${customerId}`);
      setHistory(Array.isArray(res.data) ? res.data : []);
    } catch (loadError) {
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const loadManagement = useCallback(async () => {
    const params = {};
    if (managementStart) params.inicio = managementStart;
    if (managementEnd) params.fim = managementEnd;

    try {
      setLoadingManagement(true);
      const res = await axios.get(`${apiBaseUrl}/nps/management`, { params });
      const payload = res.data || {};
      setManagement({
        summary: payload.summary || {},
        matrix: Array.isArray(payload.matrix) ? payload.matrix : [],
        rows: Array.isArray(payload.rows) ? payload.rows : [],
        questions: Array.isArray(payload.questions) ? payload.questions : [],
        question_stats: Array.isArray(payload.question_stats) ? payload.question_stats : [],
      });
    } catch (loadError) {
      setManagement({
        summary: {},
        matrix: [],
        rows: [],
        questions: [],
        question_stats: [],
      });
    } finally {
      setLoadingManagement(false);
    }
  }, [managementEnd, managementStart]);

  useEffect(() => {
    loadQuestions();
    loadPending();
  }, [loadPending, loadQuestions]);

  useEffect(() => {
    loadManagement();
  }, [loadManagement]);

  useEffect(() => {
    if (!selected || !primaryQuestion) return;
    const key = String(primaryQuestion.id);
    setAnswerValues((current) => {
      if (current[key] !== undefined && String(current[key]).trim() !== '') {
        return current;
      }
      if (selected.rating === null || selected.rating === undefined) {
        return current;
      }
      return { ...current, [key]: String(selected.rating) };
    });
  }, [primaryQuestion, selected]);

  const clearFeedback = () => {
    setMessage('');
    setError('');
  };

  const handleSelectPending = async (item) => {
    clearFeedback();
    setSelected(item);
    setAnswerValues(normalizeAnswerMap(item.answers));
    setFeedback(item.feedback || '');
    loadHistory(item.customer_id);

    try {
      const res = await axios.get(`${apiBaseUrl}/nps/tickets/${encodeURIComponent(item.ticket_number)}`);
      const ticketNps = res.data || {};
      setAnswerValues((current) => ({
        ...current,
        ...normalizeAnswerMap(ticketNps.answers),
      }));
      if (ticketNps.feedback !== null && ticketNps.feedback !== undefined) {
        setFeedback(ticketNps.feedback || '');
      }
      if (primaryQuestion && ticketNps.rating !== null && ticketNps.rating !== undefined) {
        setAnswerValues((current) => ({
          ...current,
          [String(primaryQuestion.id)]: String(ticketNps.rating),
        }));
      }
    } catch (loadError) {
      // noop
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswerValues((current) => ({ ...current, [String(questionId)]: value }));
  };

  const prepareSubmission = () => {
    const payloadAnswers = [];
    const missingRequired = [];
    let ratingValue = null;

    activeQuestions.forEach((question) => {
      const key = String(question.id);
      const rawValue = answerValues[key];
      const normalizedRaw = typeof rawValue === 'string' ? rawValue.trim() : rawValue;

      if ((normalizedRaw === '' || normalizedRaw === null || normalizedRaw === undefined) && question.is_required) {
        missingRequired.push(question.question_text || `Pergunta ${question.id}`);
        return;
      }

      if (normalizedRaw === '' || normalizedRaw === null || normalizedRaw === undefined) {
        return;
      }

      if (question.question_type === 'score_0_10') {
        const score = Number(normalizedRaw);
        if (!Number.isInteger(score) || score < 0 || score > 10) {
          missingRequired.push(`${question.question_text || 'Nota'} (valor entre 0 e 10)`);
          return;
        }
        payloadAnswers.push({
          question_id: question.id,
          answer: score,
          answer_numeric: score,
        });
        if (primaryQuestion && primaryQuestion.id === question.id) {
          ratingValue = score;
        }
        return;
      }

      if (question.question_type === 'yes_no') {
        const normalizedYesNo = normalizeYesNo(normalizedRaw);
        if (!normalizedYesNo) {
          missingRequired.push(`${question.question_text || 'Pergunta'} (responda Sim ou Nao)`);
          return;
        }
        payloadAnswers.push({
          question_id: question.id,
          answer: normalizedYesNo,
          answer_text: normalizedYesNo,
        });
        return;
      }

      const textAnswer = String(normalizedRaw);
      payloadAnswers.push({
        question_id: question.id,
        answer: textAnswer,
        answer_text: textAnswer,
      });
    });

    if (ratingValue === null && primaryQuestion) {
      const primaryValue = answerValues[String(primaryQuestion.id)];
      const parsedPrimary = Number(primaryValue);
      if (Number.isInteger(parsedPrimary) && parsedPrimary >= 0 && parsedPrimary <= 10) {
        ratingValue = parsedPrimary;
      }
    }

    if (ratingValue === null && selected?.rating !== null && selected?.rating !== undefined) {
      const fallbackRating = Number(selected.rating);
      if (Number.isInteger(fallbackRating) && fallbackRating >= 0 && fallbackRating <= 10) {
        ratingValue = fallbackRating;
      }
    }

    if (missingRequired.length > 0) {
      return {
        ok: false,
        message: `Preencha os campos obrigatorios: ${missingRequired.slice(0, 3).join(', ')}${missingRequired.length > 3 ? '...' : ''}`,
      };
    }

    if (ratingValue === null) {
      return {
        ok: false,
        message: 'Defina uma nota valida de 0 a 10 para calcular o NPS.',
      };
    }

    return {
      ok: true,
      rating: ratingValue,
      answers: payloadAnswers,
    };
  };

  const handleSaveNps = async () => {
    if (!selected) {
      setError('Selecione um ticket para registrar o NPS.');
      return;
    }

    clearFeedback();
    const prepared = prepareSubmission();
    if (!prepared.ok) {
      setError(prepared.message);
      return;
    }

    try {
      setSavingNps(true);
      await axios.post(`${apiBaseUrl}/tickets/${encodeURIComponent(selected.ticket_number)}/nps`, {
        rating: prepared.rating,
        feedback: feedback.trim() || null,
        answers: prepared.answers,
        finalize: true,
        atendente: localStorage.getItem('attendantName') || 'Atendente',
      });
      setMessage(`NPS registrado para o ticket #${selected.ticket_number} e atendimento finalizado.`);
      const customerId = selected.customer_id;
      setSelected(null);
      setAnswerValues({});
      setFeedback('');
      await Promise.all([
        loadPending(),
        loadManagement(),
        customerId ? loadHistory(customerId) : Promise.resolve(),
      ]);
    } catch (saveError) {
      setError(saveError?.response?.data?.message || 'Falha ao salvar o NPS.');
    } finally {
      setSavingNps(false);
    }
  };

  const refreshQuestionData = useCallback(async () => {
    await Promise.all([loadQuestions(), loadManagement()]);
  }, [loadManagement, loadQuestions]);

  const handleCreateQuestion = async (event) => {
    event.preventDefault();
    clearFeedback();
    const questionText = (newQuestion.question_text || '').trim();
    if (!questionText) {
      setError('Informe o texto da pergunta.');
      return;
    }

    try {
      setCreatingQuestion(true);
      await axios.post(`${apiBaseUrl}/nps/questions`, {
        question_text: questionText,
        question_type: newQuestion.is_primary ? 'score_0_10' : newQuestion.question_type,
        is_required: newQuestion.is_primary ? true : Boolean(newQuestion.is_required),
        is_active: Boolean(newQuestion.is_active),
        is_primary: Boolean(newQuestion.is_primary),
      });
      setNewQuestion({
        question_text: '',
        question_type: 'text',
        is_required: false,
        is_active: true,
        is_primary: false,
      });
      setMessage('Pergunta criada com sucesso.');
      await refreshQuestionData();
    } catch (createError) {
      setError(createError?.response?.data?.message || 'Falha ao criar pergunta.');
    } finally {
      setCreatingQuestion(false);
    }
  };

  const updateQuestion = async (questionId, payload, successMessage) => {
    clearFeedback();
    try {
      setQuestionBusyId(questionId);
      await axios.put(`${apiBaseUrl}/nps/questions/${questionId}`, payload);
      setMessage(successMessage || 'Pergunta atualizada.');
      await refreshQuestionData();
    } catch (updateError) {
      setError(updateError?.response?.data?.message || 'Falha ao atualizar pergunta.');
    } finally {
      setQuestionBusyId(null);
    }
  };

  const deleteQuestion = async (question) => {
    if (!question?.id) return;
    const confirmed = window.confirm(`Excluir a pergunta "${question.question_text}"?`);
    if (!confirmed) return;

    clearFeedback();
    try {
      setQuestionBusyId(question.id);
      await axios.delete(`${apiBaseUrl}/nps/questions/${question.id}`);
      setMessage('Pergunta removida.');
      await refreshQuestionData();
    } catch (deleteError) {
      setError(deleteError?.response?.data?.message || 'Falha ao remover pergunta.');
    } finally {
      setQuestionBusyId(null);
    }
  };

  const moveQuestion = async (questionId, direction) => {
    const ids = orderedQuestions.map((question) => question.id);
    const currentIndex = ids.indexOf(questionId);
    const targetIndex = currentIndex + direction;
    if (currentIndex < 0 || targetIndex < 0 || targetIndex >= ids.length) {
      return;
    }

    const nextOrder = [...ids];
    [nextOrder[currentIndex], nextOrder[targetIndex]] = [nextOrder[targetIndex], nextOrder[currentIndex]];

    clearFeedback();
    try {
      setQuestionBusyId(questionId);
      await axios.put(`${apiBaseUrl}/nps/questions/reorder`, {
        ordered_ids: nextOrder,
      });
      setMessage('Ordem das perguntas atualizada.');
      await refreshQuestionData();
    } catch (reorderError) {
      setError(reorderError?.response?.data?.message || 'Falha ao reordenar perguntas.');
    } finally {
      setQuestionBusyId(null);
    }
  };

  const editQuestionText = async (question) => {
    const draft = window.prompt('Editar texto da pergunta', question.question_text || '');
    if (draft === null) return;
    const nextText = draft.trim();
    if (!nextText) {
      setError('O texto da pergunta nao pode ficar vazio.');
      return;
    }
    await updateQuestion(question.id, { question_text: nextText }, 'Texto da pergunta atualizado.');
  };

  const handleSeedDemo = async () => {
    clearFeedback();
    try {
      setSeedLoading(true);
      const res = await axios.post(`${apiBaseUrl}/nps/demo-seed`, {
        count: 8,
      });
      const created = res.data?.created ?? 0;
      setMessage(`Seed concluido: ${created} pesquisa(s) demo criada(s).`);
      await Promise.all([loadPending(), loadManagement()]);
      if (selected?.customer_id) {
        await loadHistory(selected.customer_id);
      }
    } catch (seedError) {
      setError(seedError?.response?.data?.message || 'Falha ao gerar dados demo de NPS.');
    } finally {
      setSeedLoading(false);
    }
  };

  const renderQuestionInput = (question) => {
    const value = answerValues[String(question.id)] ?? '';

    if (question.question_type === 'score_0_10') {
      return (
        <input
          type="number"
          min="0"
          max="10"
          value={value}
          onChange={(event) => handleAnswerChange(question.id, event.target.value)}
          placeholder="0 a 10"
        />
      );
    }

    if (question.question_type === 'yes_no') {
      return (
        <select value={value} onChange={(event) => handleAnswerChange(question.id, event.target.value)}>
          <option value="">Selecione...</option>
          <option value="Sim">Sim</option>
          <option value="Nao">Nao</option>
        </select>
      );
    }

    if (question.question_type === 'textarea') {
      return (
        <textarea
          rows="3"
          value={value}
          onChange={(event) => handleAnswerChange(question.id, event.target.value)}
          placeholder="Resposta do cliente"
        />
      );
    }

    return (
      <input
        type="text"
        value={value}
        onChange={(event) => handleAnswerChange(question.id, event.target.value)}
        placeholder="Resposta do cliente"
      />
    );
  };

  return (
    <div className="nps-manager">
      <header className="nps-header">
        <div>
          <h2>Central de Satisfacao do Cliente</h2>
          <p>Configure sua pesquisa, execute a coleta com o time e acompanhe o NPS com visao comercial.</p>
        </div>
        <div className="nps-actions">
          <button className="ghost" onClick={() => { loadPending(); loadQuestions(); loadManagement(); }}>
            Atualizar painel
          </button>
          <button className="warn" onClick={handleSeedDemo} disabled={seedLoading}>
            {seedLoading ? 'Gerando...' : 'Gerar dados de exemplo'}
          </button>
        </div>
      </header>

      {(message || error) && (
        <div className={`nps-alert ${error ? 'is-error' : 'is-success'}`}>
          {error || message}
        </div>
      )}

      <div className="nps-view-switch">
        {VIEW_OPTIONS.map((view) => (
          <button
            key={view.id}
            type="button"
            className={activeView === view.id ? 'active' : ''}
            onClick={() => setActiveView(view.id)}
          >
            {view.label}
          </button>
        ))}
      </div>

      {activeView === 'coleta' && (
        <div className="nps-grid nps-grid--coleta">
          <section className="nps-card">
            <div className="nps-card-head">
              <h3>Fila de Coleta NPS</h3>
              <span>{filteredPending.length} ticket(s)</span>
            </div>
            <div className="nps-pending-resume">
              <span>Total: {pendingSummary.total}</span>
              <span>Imediata: {pendingSummary.imediata}</span>
              <span>Retroativa sem NPS: {pendingSummary.retroativa}</span>
            </div>
            <div className="nps-filter-row nps-filter-row--compact">
              <select value={pendingMode} onChange={(event) => setPendingMode(event.target.value)}>
                <option value="all">Todas filas de coleta</option>
                <option value="imediata">Apenas em finalizacao</option>
                <option value="retroativa">Apenas resolvido/finalizado sem NPS</option>
              </select>
            </div>
            <input
              type="text"
              className="nps-search"
              placeholder="Buscar ticket, cliente ou telefone"
              value={pendingSearch}
              onChange={(event) => setPendingSearch(event.target.value)}
            />
            <div className="nps-list">
              {loadingPending && <div className="nps-empty">Carregando pendentes...</div>}
              {!loadingPending && filteredPending.length === 0 && (
                <div className="nps-empty">
                  Nenhum ticket para esta fila. A coleta mostra itens em finalizacao e tambem tickets resolvidos/finalizados sem pesquisa.
                </div>
              )}
              {!loadingPending && filteredPending.map((item) => (
                <button
                  key={`${item.ticket_number}-${item.chat_id}`}
                  type="button"
                  className={`nps-item ${selected?.ticket_number === item.ticket_number ? 'active' : ''}`}
                  onClick={() => handleSelectPending(item)}
                >
                  <div className="nps-item-top">
                    <strong>#{item.ticket_number}</strong>
                    <span className={`nps-chip status-${item.status || 'sem_status'}`}>{item.status || 'sem_status'}</span>
                  </div>
                  <div className="nps-item-flow">
                    {item.queue_reason === 'coleta_imediata' ? 'Fila: Coleta imediata' : 'Fila: Retroativa sem NPS'}
                  </div>
                  <div className="nps-item-meta">{item.customer_name || 'Cliente'}</div>
                  <small>{item.customer_phone || 'Sem telefone'}</small>
                </button>
              ))}
            </div>
          </section>

          <section className="nps-card">
            <div className="nps-card-head">
              <h3>Formulario de Coleta</h3>
              <span>{selected ? `#${selected.ticket_number}` : 'Sem selecao'}</span>
            </div>
            {!selected && <div className="nps-empty">Selecione um ticket para registrar a pesquisa.</div>}
            {selected && (
              <div className="nps-form">
                <div className="nps-ticket-meta">
                  <div><strong>Cliente:</strong> {selected.customer_name || 'Cliente'}</div>
                  <div><strong>Telefone:</strong> {selected.customer_phone || 'Sem telefone'}</div>
                </div>
                {!primaryQuestion && (
                  <div className="nps-empty">Defina uma pergunta principal de Nota 0-10 na aba Perguntas.</div>
                )}
                {activeQuestions.map((question) => (
                  <label key={question.id} className="nps-question-field">
                    <span>
                      {question.question_text}
                      {question.is_required ? ' *' : ''}
                    </span>
                    <small>{QUESTION_TYPE_LABEL[question.question_type] || question.question_type}</small>
                    {renderQuestionInput(question)}
                  </label>
                ))}
                <label className="nps-question-field">
                  <span>Feedback adicional (opcional)</span>
                  <textarea
                    rows="3"
                    value={feedback}
                    onChange={(event) => setFeedback(event.target.value)}
                    placeholder="Resumo do cliente"
                  />
                </label>
                <button onClick={handleSaveNps} disabled={savingNps || !selected}>
                  {savingNps ? 'Salvando...' : 'Salvar NPS e finalizar ticket'}
                </button>
              </div>
            )}
          </section>

          <section className="nps-card">
            <div className="nps-card-head">
              <h3>Historico do Cliente</h3>
              <span>{history.length} pesquisa(s)</span>
            </div>
            {loadingHistory && <div className="nps-empty">Carregando historico...</div>}
            {!loadingHistory && !selected && <div className="nps-empty">Selecione um ticket para ver o historico.</div>}
            {!loadingHistory && selected && history.length === 0 && (
              <div className="nps-empty">Sem pesquisas anteriores para este cliente.</div>
            )}
            {!loadingHistory && selected && history.length > 0 && (
              <div className="nps-history-list">
                {history.map((item) => (
                  <article key={item.id} className="nps-history-item">
                    <div className="nps-history-top">
                      <strong>#{item.ticket_number}</strong>
                      <span className={`nps-chip nps-chip--${item.classification || 'nao_classificado'}`}>
                        {CLASSIFICATION_LABEL[item.classification] || 'Nao classificado'}
                      </span>
                    </div>
                    <p><strong>Nota:</strong> {item.rating ?? '-'}</p>
                    <p>{item.feedback || 'Sem feedback adicional.'}</p>
                    <small>{formatDateTime(item.timestamp)}</small>
                    {Array.isArray(item.answers) && item.answers.length > 0 && (
                      <ul className="nps-answer-list">
                        {item.answers.map((answer) => (
                          <li key={`${item.id}-${answer.question_id}`}>
                            <strong>{answer.question_text}:</strong>{' '}
                            {answer.answer_numeric !== null && answer.answer_numeric !== undefined
                              ? answer.answer_numeric
                              : (answer.answer_text || answer.answer || '-')}
                          </li>
                        ))}
                      </ul>
                    )}
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {activeView === 'perguntas' && (
        <div className="nps-grid nps-grid--questions">
          <section className="nps-card">
            <div className="nps-card-head">
              <h3>Nova Pergunta</h3>
              <span>Editor comercial</span>
            </div>
            <form className="nps-form" onSubmit={handleCreateQuestion}>
              <label className="nps-question-field">
                <span>Pergunta</span>
                <input
                  type="text"
                  value={newQuestion.question_text}
                  onChange={(event) => setNewQuestion((prev) => ({ ...prev, question_text: event.target.value }))}
                  placeholder="Ex.: Como voce avalia a clareza do atendimento?"
                />
              </label>
              <label className="nps-question-field">
                <span>Tipo</span>
                <select
                  value={newQuestion.question_type}
                  onChange={(event) => setNewQuestion((prev) => ({ ...prev, question_type: event.target.value }))}
                  disabled={newQuestion.is_primary}
                >
                  {QUESTION_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <div className="nps-toggle-grid">
                <label className={`nps-toggle-card ${(newQuestion.is_required || newQuestion.is_primary) ? 'is-on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={newQuestion.is_required || newQuestion.is_primary}
                    onChange={(event) => setNewQuestion((prev) => ({ ...prev, is_required: event.target.checked }))}
                    disabled={newQuestion.is_primary}
                  />
                  <span className="nps-toggle-track" />
                  <span className="nps-toggle-copy">
                    <strong>Obrigatoria</strong>
                    <small>Exige resposta antes de concluir a pesquisa.</small>
                  </span>
                </label>
                <label className={`nps-toggle-card ${newQuestion.is_active ? 'is-on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={newQuestion.is_active}
                    onChange={(event) => setNewQuestion((prev) => ({ ...prev, is_active: event.target.checked }))}
                  />
                  <span className="nps-toggle-track" />
                  <span className="nps-toggle-copy">
                    <strong>Ativa</strong>
                    <small>Pergunta aparece no formulario de coleta.</small>
                  </span>
                </label>
                <label className={`nps-toggle-card ${newQuestion.is_primary ? 'is-on is-primary' : ''}`}>
                  <input
                    type="checkbox"
                    checked={newQuestion.is_primary}
                    onChange={(event) => setNewQuestion((prev) => ({ ...prev, is_primary: event.target.checked }))}
                  />
                  <span className="nps-toggle-track" />
                  <span className="nps-toggle-copy">
                    <strong>Pergunta principal (Nota 0-10)</strong>
                    <small>Base oficial do calculo NPS (promotor, neutro e detrator).</small>
                  </span>
                </label>
              </div>
              <button type="submit" disabled={creatingQuestion}>
                {creatingQuestion ? 'Criando...' : 'Salvar pergunta'}
              </button>
            </form>
          </section>

          <section className="nps-card">
            <div className="nps-card-head">
              <h3>Ordem e Regras</h3>
              <span>{orderedQuestions.length} pergunta(s) ativas/inativas</span>
            </div>
            {loadingQuestions && <div className="nps-empty">Carregando perguntas...</div>}
            {!loadingQuestions && orderedQuestions.length === 0 && <div className="nps-empty">Nenhuma pergunta cadastrada.</div>}
            {!loadingQuestions && orderedQuestions.length > 0 && (
              <div className="nps-question-list">
                {orderedQuestions.map((question, index) => (
                  <article key={question.id} className={`nps-question-item ${!question.is_active ? 'is-inactive' : ''}`}>
                    <div className="nps-question-top">
                      <strong>{question.question_text}</strong>
                      <div className="nps-question-tags">
                        <span className="nps-chip">{QUESTION_TYPE_LABEL[question.question_type] || question.question_type}</span>
                        {question.is_primary && <span className="nps-chip nps-chip--promotor">Principal</span>}
                        {!question.is_active && <span className="nps-chip nps-chip--muted">Inativa</span>}
                      </div>
                    </div>
                    <div className="nps-question-controls">
                      <button
                        type="button"
                        className="ghost"
                        disabled={questionBusyId === question.id || index === 0}
                        onClick={() => moveQuestion(question.id, -1)}
                      >
                        Subir
                      </button>
                      <button
                        type="button"
                        className="ghost"
                        disabled={questionBusyId === question.id || index === orderedQuestions.length - 1}
                        onClick={() => moveQuestion(question.id, 1)}
                      >
                        Descer
                      </button>
                      <button
                        type="button"
                        className="ghost"
                        disabled={questionBusyId === question.id}
                        onClick={() => editQuestionText(question)}
                      >
                        Editar
                      </button>
                      <select
                        value={question.question_type}
                        disabled={questionBusyId === question.id || question.is_primary}
                        onChange={(event) => updateQuestion(question.id, { question_type: event.target.value }, 'Tipo atualizado.')}
                      >
                        {QUESTION_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      <label className="nps-toggle compact">
                        <input
                          type="checkbox"
                          checked={question.is_required || question.is_primary}
                          disabled={questionBusyId === question.id || question.is_primary}
                          onChange={(event) => updateQuestion(question.id, { is_required: event.target.checked }, 'Regra de obrigatoriedade atualizada.')}
                        />
                        <span>Obrigatoria</span>
                      </label>
                      <label className="nps-toggle compact">
                        <input
                          type="checkbox"
                          checked={question.is_active}
                          disabled={questionBusyId === question.id}
                          onChange={(event) => updateQuestion(question.id, { is_active: event.target.checked }, 'Status da pergunta atualizado.')}
                        />
                        <span>Ativa</span>
                      </label>
                      <button
                        type="button"
                        className="ghost"
                        disabled={questionBusyId === question.id || question.is_primary}
                        onClick={() => updateQuestion(question.id, { is_primary: true }, 'Pergunta principal atualizada.')}
                      >
                        Tornar principal
                      </button>
                      <button
                        type="button"
                        className="danger"
                        disabled={questionBusyId === question.id}
                        onClick={() => deleteQuestion(question)}
                      >
                        Excluir
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {activeView === 'gestao' && (
        <div className="nps-grid nps-grid--management">
          <section className="nps-card">
            <div className="nps-card-head">
              <h3>Resumo NPS</h3>
              <span>Painel executivo</span>
            </div>
            <div className="nps-filter-row">
              <label>
                Inicio
                <input type="date" value={managementStart} onChange={(event) => setManagementStart(event.target.value)} />
              </label>
              <label>
                Fim
                <input type="date" value={managementEnd} onChange={(event) => setManagementEnd(event.target.value)} />
              </label>
              <button className="ghost" onClick={loadManagement} disabled={loadingManagement}>
                {loadingManagement ? 'Atualizando...' : 'Aplicar'}
              </button>
            </div>

            <div className="nps-summary-grid">
              <article>
                <strong>{management.summary?.total_responses ?? 0}</strong>
                <span>Respostas registradas</span>
              </article>
              <article>
                <strong>{management.summary?.average_rating ?? 0}</strong>
                <span>Nota media</span>
              </article>
              <article>
                <strong>{management.summary?.nps_score ?? 0}</strong>
                <span>Score NPS</span>
              </article>
              <article>
                <strong>{management.summary?.promoters ?? 0}</strong>
                <span>Promotores</span>
              </article>
              <article>
                <strong>{management.summary?.neutrals ?? 0}</strong>
                <span>Neutros</span>
              </article>
              <article>
                <strong>{management.summary?.detractors ?? 0}</strong>
                <span>Detratores</span>
              </article>
              <article>
                <strong>{management.summary?.coverage_percent ?? 0}%</strong>
                <span>Taxa de cobertura</span>
              </article>
              <article>
                <strong>{management.summary?.adherence_percent ?? 0}%</strong>
                <span>Taxa de aderencia</span>
              </article>
              <article>
                <strong>{management.summary?.required_completion_percent ?? 0}%</strong>
                <span>Preenchimento obrigatorio</span>
              </article>
              <article>
                <strong>{management.summary?.pending_collection_tickets ?? 0}</strong>
                <span>Pendentes sem NPS</span>
              </article>
              <article>
                <strong>{management.summary?.responded_tickets ?? 0}/{management.summary?.eligible_tickets ?? 0}</strong>
                <span>Tickets respondidos/elegiveis</span>
              </article>
              <article>
                <strong>{management.summary?.required_questions_count ?? 0}</strong>
                <span>Perguntas obrigatorias</span>
              </article>
            </div>

            <div className="nps-matrix">
              {(management.matrix || []).map((item) => (
                <div key={item.bucket} className={`nps-matrix-item nps-matrix-item--${item.bucket}`}>
                  <div className="nps-matrix-top">
                    <strong>{item.label}</strong>
                    <span>{item.percent?.toFixed ? item.percent.toFixed(2) : item.percent}%</span>
                  </div>
                  <div className="nps-progress">
                    <div className="nps-progress-bar" style={{ width: `${Math.max(0, Math.min(100, Number(item.percent) || 0))}%` }} />
                  </div>
                  <small>{item.count || 0} resposta(s)</small>
                </div>
              ))}
              {(management.matrix || []).length === 0 && <div className="nps-empty">Sem dados para o periodo selecionado.</div>}
            </div>
          </section>

          <section className="nps-card">
            <div className="nps-card-head">
              <h3>Estatistica por Pergunta</h3>
              <span>{management.question_stats?.length || 0} item(ns)</span>
            </div>
            <div className="nps-table-wrap">
              <div className="nps-table nps-table--questions">
                <div className="nps-table-head">
                  <span>Pergunta</span>
                  <span>Tipo</span>
                  <span>Respostas</span>
                  <span>Media Num.</span>
                  <span>Sim</span>
                  <span>Nao</span>
                </div>
                {(management.question_stats || []).map((stat) => (
                  <div key={stat.question_id} className="nps-table-row">
                    <span>{stat.question_text}</span>
                    <span>{QUESTION_TYPE_LABEL[stat.question_type] || stat.question_type}</span>
                    <span>{stat.responses || 0}</span>
                    <span>{stat.numeric_avg ?? '-'}</span>
                    <span>{stat.yes_count || 0}</span>
                    <span>{stat.no_count || 0}</span>
                  </div>
                ))}
                {(management.question_stats || []).length === 0 && <div className="nps-empty">Sem estatisticas.</div>}
              </div>
            </div>
          </section>

          <section className="nps-card">
            <div className="nps-card-head">
              <h3>Historico de Respostas</h3>
              <span>{filteredManagementRows.length} registro(s)</span>
            </div>
            <div className="nps-filter-row nps-filter-row--compact">
              <input
                type="text"
                placeholder="Buscar por ticket, cliente, telefone ou feedback"
                value={managementSearch}
                onChange={(event) => setManagementSearch(event.target.value)}
              />
              <select value={managementClassification} onChange={(event) => setManagementClassification(event.target.value)}>
                <option value="all">Todas classificacoes</option>
                <option value="promotor">Promotor</option>
                <option value="neutro">Neutro</option>
                <option value="detrator">Detrator</option>
              </select>
            </div>
            <div className="nps-table-wrap">
              <div className="nps-table nps-table--history">
                <div className="nps-table-head">
                  <span>Ticket</span>
                  <span>Cliente</span>
                  <span>Nota</span>
                  <span>Classificacao</span>
                  <span>Status Ticket</span>
                  <span>Feedback</span>
                  <span>Data</span>
                </div>
                {filteredManagementRows.map((row) => (
                  <div key={row.id} className="nps-table-row">
                    <span>#{row.ticket_number}</span>
                    <span>{row.customer_name || row.customer_phone || 'Cliente'}</span>
                    <span>{row.rating ?? '-'}</span>
                    <span>
                      <span className={`nps-chip nps-chip--${row.classification || 'nao_classificado'}`}>
                        {CLASSIFICATION_LABEL[row.classification] || 'Nao classificado'}
                      </span>
                    </span>
                    <span>{row.ticket_status || '-'}</span>
                    <span>
                      <strong>{row.feedback || '-'}</strong>
                      {Array.isArray(row.answers) && row.answers.length > 0 && (
                        <small className="nps-row-answers">
                          {row.answers
                            .slice(0, 2)
                            .map((answer) => {
                              const answerValue = answer.answer_numeric !== null && answer.answer_numeric !== undefined
                                ? answer.answer_numeric
                                : (answer.answer_text || answer.answer || '-');
                              return `${answer.question_text}: ${answerValue}`;
                            })
                            .join(' | ')}
                          {row.answers.length > 2 ? ` | +${row.answers.length - 2}` : ''}
                        </small>
                      )}
                    </span>
                    <span>{formatDateTime(row.timestamp)}</span>
                  </div>
                ))}
                {filteredManagementRows.length === 0 && <div className="nps-empty">Sem respostas para os filtros aplicados.</div>}
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default NpsManager;
